import { useContext, useCallback, memo, useEffect } from 'react';
import { StationWrapper, IconWrapper, TimelineWrapper, ZoneStartStationAnimation } from './Station.styled';
import { ZoneContext } from 'context/ZoneContext';
import StationIcon from 'components/common/StationIcon';
import { Station, StationStatus, StationType } from 'pages/Map/types';
import BigJuncture, { BigJunctureStation } from './components/BigJuncture';
import { SubZoneStation } from './components/SubZone';
import { SubZone } from './components/SubZone/SubZone';
import { Description } from '.';
import { useRegionContext } from 'context/RegionContext';
import { useStorage } from '@medly-components/utils';
import { MapTourContext } from 'context/MapTourContext';
import { TourType } from 'pages/Map/components/MapTour';
import { UserContext } from 'context/UserContext';
import { zIndex } from 'theme/zIndex';
import { shouldIncreaseZIndexOfPathStation } from '../PathRenderer/PathDescription/utils';
import useMapSearch from 'hooks/search/useMapSearch';
import { MapSearchContext } from 'context/MapSearchContext';

const INTERACTED_WITH_ZONE_START_KEY_PREFIX = 'interacted-with-zone-start-of';
export const StationRenderer = memo(({ station }: { station: Station }) => {
    const {
        setSelectedStation,
        activeStation,
        paths,
        selectedPath,
        expandedStation,
        zone,
        expandedPathDescription,
        expandedDescriptionStation,
        setExpandedDescriptionStation,
        selectedStation
    } = useContext(ZoneContext);
    const { region } = useRegionContext();
    const { getKeyWithUserId } = useContext(UserContext);
    const { tour, isMapTour, isCurrentTourItem } = useContext(MapTourContext);
    const isDescriptionExpanded = expandedDescriptionStation?.id === station.id;
    const defaultZIndex = station.getDefaultZIndex(activeStation);
    const stationZIndex = isDescriptionExpanded ? station.getHoveredZIndex(defaultZIndex) : defaultZIndex;
    const isZoneStartStation = station.type === StationType.ZoneStart;
    const zoneStartInteractionKey = `${INTERACTED_WITH_ZONE_START_KEY_PREFIX}-${region.name}-${zone.name}`;
    const [isZoneStartInteracted, setIsZoneStartInteracted] = useStorage<boolean>(getKeyWithUserId(zoneStartInteractionKey));
    const { getSearchColor } = useMapSearch();
    const { activeSearchId } = useContext(MapSearchContext);

    const onDescriptionExpand = useCallback(() => {
        setExpandedDescriptionStation(station);
    }, []);

    const onDescriptionCollapse = useCallback(() => {
        setExpandedDescriptionStation(undefined);
    }, []);

    const onStationClick = useCallback(() => {
        if (isZoneStartStation) {
            setIsZoneStartInteracted(true);
        }

        setSelectedStation(station);
    }, [station, setSelectedStation]);

    //opens drawer whenever we select station search result
    useEffect(() => {
        if (station.id !== activeSearchId) return;
        setSelectedStation(station);
    }, [activeSearchId]);

    if (station.isHidden(expandedStation)) {
        return null;
    }

    if (station instanceof SubZoneStation) {
        return <SubZone station={station} />;
    }

    if (station instanceof BigJunctureStation) {
        return <BigJuncture station={station} />;
    }

    const stationStatus = station.getStationStatus(activeStation, paths, selectedStation);
    const style = {
        left: station.getAbsoluteXValue(),
        top: station.getAbsoluteYValue(),
        zIndex: shouldIncreaseZIndexOfPathStation(station, paths, expandedPathDescription) ? zIndex.PathDescription : stationZIndex
    };

    const activePath = selectedPath ? selectedPath : isMapTour ? tour?.path : undefined;
    const selectedPathIntersectionDirection = activePath ? station.getStationInterasectionDirectionForSelectedPath(activePath) : null;
    const timeline = station.getTimeline();
    const showStartStationAnimation = station.type === StationType.ZoneStart;
    const backgroundColor = getSearchColor(station.id) || station.getStationColor(stationStatus);

    return (
        <StationWrapper style={style} id={station.id} isMapTourActiveElement={isCurrentTourItem(TourType.INFO_ABOUT_STATIONS)}>
            {showStartStationAnimation && !isZoneStartInteracted ? <ZoneStartStationAnimation data-testid="zone-start-animation" /> : null}
            <IconWrapper
                onClick={onStationClick}
                onMouseEnter={onDescriptionExpand}
                onMouseLeave={onDescriptionCollapse}
                style={{ backgroundColor }}
                selectedPathIntersectionDirection={selectedPathIntersectionDirection}
                isPathSelectedInTour={isCurrentTourItem(TourType.INFO_ABOUT_PATHS)}
            >
                <StationIcon type={station.getType()} isHighlight={isDescriptionExpanded || station.isSelectedStation(selectedStation)} />
            </IconWrapper>
            <Description
                onDescriptionExpand={onDescriptionExpand}
                onDescriptionCollapse={onDescriptionCollapse}
                station={station}
                isActiveStation={stationStatus === StationStatus.ACTIVE_STATION}
                onClick={onStationClick}
            />
            {timeline ? <TimelineWrapper textVariant="body3">{timeline}</TimelineWrapper> : null}
        </StationWrapper>
    );
});
